"use client";

import { ArrowUpRight, Loader, Mail, MapPin, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const contactMethods = [{
  label: "OFFICE",
  value: "123 Advocate Way, Suite 100, City, State",
  icon: MapPin
}, {
  label: "EMAIL",
  value: "support@priceadvocate.com",
  icon: Mail
}, {
  label: "PHONE",
  value: "+1 (800) 555-0199",
  icon: Phone
}];
function ReachOutForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-blue-900 bg-blue-50">
        <p className="font-default text-xs tracking-widest uppercase text-blue-900 mb-3">
          [ MESSAGE RECEIVED ]
        </p>
        <p className="font-default text-xl text-gray-900 mb-6 leading-snug">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="font-default text-xs tracking-widest uppercase border-gray-300 bg-transparent hover:bg-blue-100 text-gray-900">
          Send Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-7">
      <FormField control={control} name="inquiry_type" rules={{
      required: "Please select an inquiry type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.2em] uppercase text-gray-500">
              01 / Reason for Contact
            </FormLabel>
            <Select onValueChange={field.onChange} value={field.value || ""}>
              <FormControl>
                <SelectTrigger className="w-full h-12 bg-transparent border-0 border-b border-gray-300 px-0 font-default text-base text-gray-900 focus-visible:ring-0 focus-visible:border-blue-900">
                  <SelectValue placeholder="Select an option" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-white border-gray-200">
                <SelectItem value="General Inquiry" className="font-default">General Inquiry</SelectItem>
                <SelectItem value="Negotiation Help" className="font-default">Negotiation Help</SelectItem>
                <SelectItem value="Pricing Consultation" className="font-default">Pricing Consultation</SelectItem>
                <SelectItem value="Partnership" className="font-default">Partnership</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-7">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] tracking-[0.2em] uppercase text-gray-500">
                02 / Your Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="h-12 bg-transparent border-0 border-b border-gray-300 px-0 font-default text-base text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-900" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[10px] tracking-[0.2em] uppercase text-gray-500">
                03 / Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="h-12 bg-transparent border-0 border-b border-gray-300 px-0 font-default text-base text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-900" />
              </FormControl>
              <FormMessage className="font-default text-xs" />
            </FormItem>} />
      </div>

      <FormField control={control} name="phone" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.2em] uppercase text-gray-500">
              04 / Phone Number — Optional
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="+1 (555) 123-4567" {...field} className="h-12 bg-transparent border-0 border-b border-gray-300 px-0 font-default text-base text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-900" />
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Min 10 characters"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[10px] tracking-[0.2em] uppercase text-gray-500">
              05 / Message
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="Tell us about your purchase..." {...field} className="min-h-[140px] bg-transparent border-0 border-b border-gray-300 px-0 py-3 font-default text-base text-gray-900 placeholder:text-gray-400 focus-visible:ring-0 focus-visible:border-blue-900 resize-none" />
            </FormControl>
            <FormMessage className="font-default text-xs" />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-600 bg-red-50">
          <p className="font-default text-xs text-red-600">{submitError}</p>
        </div>}

      <div className="pt-2">
        <p className="font-default text-[10px] tracking-[0.2em] uppercase text-gray-500 mb-4">
          [ We typically respond within 24 hours ]
        </p>
        <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-orange-600 text-white hover:bg-orange-700 font-default text-sm font-bold tracking-[0.15em] uppercase rounded-lg shadow-md">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Sending...
            </> : <>Send Message →</>}
        </Button>
      </div>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.1
  });
  return <section data-section-id="4170" id="get-in-touch" className="bg-white text-gray-900 relative overflow-hidden py-20 md:py-28 lg:py-32 px-4 sm:px-6 lg:px-10">
      <div ref={ref} className={`max-w-[1400px] mx-auto transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6 flex flex-col">
            <div className="flex items-center gap-3 mb-10">
              <span className="h-px w-10 bg-blue-900" />
              <p className="font-default text-xs tracking-[0.25em] uppercase text-blue-900">
                01 — Contact
              </p>
            </div>

            <h2 className="font-default font-bold uppercase leading-[0.9] tracking-tight text-blue-900 mb-10">
              <span className="block text-[clamp(3rem,8vw,9rem)]">READY /</span>
              <span className="block text-[clamp(3rem,8vw,9rem)]">TO /</span>
              <span className="block text-[clamp(3rem,8vw,9rem)] font-bold">
                SAVE.
              </span>
            </h2>

            <p className="font-default text-lg md:text-xl text-gray-600 leading-relaxed max-w-xl mb-12">
              Our advocates are ready to help you secure the best price on your next major purchase. Reach out today to start your negotiation journey with confidence.
            </p>

            <div className="space-y-4">
              {contactMethods.map((method, i) => {
              const Icon = method.icon;
              return <div key={i} data-index={i} className="group relative bg-gray-50 border border-gray-200 overflow-hidden transition-all duration-500 hover:bg-white shadow-sm rounded-lg">
                    <span className="absolute left-0 top-0 h-full w-0 bg-orange-600 transition-all duration-500 group-hover:w-1" />
                    <div className="flex items-center justify-between gap-6 px-6 py-6 md:px-8 md:py-7">
                      <div className="flex items-center gap-5 min-w-0">
                        <Icon className="size-4 text-blue-900 shrink-0" strokeWidth={1.5} />
                        <span className="font-default text-[11px] tracking-[0.25em] uppercase text-gray-500 shrink-0 hidden sm:inline">
                          {method.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 min-w-0 flex-1 justify-end">
                        <p className="font-default text-sm md:text-base text-gray-900 text-right truncate">
                          {method.value}
                        </p>
                        <ArrowUpRight className="size-4 text-blue-900 shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={1.5} />
                      </div>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative p-8 md:p-12 border border-gray-200 rounded-lg bg-gray-50 shadow-md">
              <div className="flex items-center justify-between mb-10 pb-6 border-b border-gray-200">
                <p className="font-default text-[10px] tracking-[0.25em] uppercase text-gray-500">
                  [ Inquiry Form ]
                </p>
                <p className="font-default text-[10px] tracking-[0.25em] uppercase text-blue-900">
                  v.01
                </p>
              </div>

              <FormProvider formKey="contact_form_about_contact" formId="contact_form_about_contact" sectionName="get-in-touch" defaultValues={{
              name: "",
              email: "",
              phone: "",
              inquiry_type: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                pattern: "phone"
              },
              inquiry_type: {
                required: true
              },
              message: {
                required: true,
                minLength: 10
              }
            }} submitText="Send Message" successMessage="Thank you for reaching out. Our team will respond within 24 hours." errorMessage="Something went wrong. Please try again or email us directly.">
                <ReachOutForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}